'use client'
import { useSearchParams } from 'next/navigation'
import React, { createContext, ReactNode, useContext, useMemo } from 'react'

import { useSmartScriptUrl } from '@/hooks/useSmartScriptUrl'
import { useUserAgent } from '@/hooks/useUserAgent'

interface OneLinkContextValue {
  oneLinkUrl: string
}

const defaultContextValue: OneLinkContextValue = {
  oneLinkUrl: '',
}

const OneLinkContext = createContext<OneLinkContextValue>(defaultContextValue)

export const useSmartScript = () => {
  const context = useContext(OneLinkContext)
  return context
}

interface OneLinkProviderProps {
  children: ReactNode
  pageName: string
  deepLinkValue: string
  afWebDp: string
  defaultURL: string
  oneLinkKey: string
}

export const OneLinkProvider = ({
  children,
  pageName,
  deepLinkValue,
  afWebDp,
  defaultURL,
  oneLinkKey,
}: OneLinkProviderProps) => {
  const searchParams = useSearchParams()
  const userAgent = useUserAgent()

  const finalAfWebDp = useMemo(() => {
    //Convert searchParams to string string (e.g., "?ref=abc")
    const searchString = searchParams?.toString()

    if (!searchString) return afWebDp

    // Check if afWebDp already has query params to append correctly
    const separator = afWebDp.includes('?') ? '&' : '?'
    return `${afWebDp}${separator}${searchString}`
  }, [searchParams, afWebDp])

  const { oneLinkUrl } = useSmartScriptUrl(oneLinkKey, {
    afWebDp: finalAfWebDp,
    deepLinkValue: deepLinkValue,
    pageName: pageName,
    defaultURL: defaultURL,
  })

  const finalOneLinkUrl = userAgent === 'ios' || userAgent === 'android' ? oneLinkUrl : finalAfWebDp

  return (
    <OneLinkContext.Provider value={{ oneLinkUrl: finalOneLinkUrl }}>
      {children}
    </OneLinkContext.Provider>
  )
}
